import { createColumnDefinitionBuilder } from 'ui-components/components/react-table-wrapper/create-column-definition-builder';
import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import {
  createExpirationDateColumnDefinition,
  createLotIdColumnDefinition,
  createProductColumnDefinition,
} from '../shared-columns/shared-columns';
import { messages as sharedMessages } from '../shared-columns/messages';
import { computeAbsoluteEaches, EACH_UOM } from '../../../../../../../constants/units-of-measure';
import { messages } from './messages';
import eachesStyles from '../product-grid/eaches-column.module.less';

import type { ReadonlyFlattenedProductRow } from '../../types';
import type { UomInfo } from '../product-search-input/types';

type ColumnRow = {
  original: ReadonlyFlattenedProductRow;
};

const LocationCell = ({ row }: { row: ColumnRow }) => {
  return <Text textType={TextType.Paragraph} value={row.original.location ?? ''} />;
};

const createLocationColumnDefinition = () =>
  createColumnDefinitionBuilder({
    Header: <Text textType={TextType.Label} value={sharedMessages.location} />,
    accessor: 'location',
    Cell: LocationCell,
    width: 150,
  })();

const QuantityCell = ({ row }: { row: ColumnRow }) => {
  const { quantity, unitOfMeasureId, unitsOfMeasure } = row.original;
  const rawUoms = unitsOfMeasure?.filter((u: UomInfo) => u.productUnitOfMeasureId !== 0) ?? [];
  const eaches = !unitOfMeasureId || unitOfMeasureId === 0 ? 1 : computeAbsoluteEaches(unitOfMeasureId, rawUoms);
  const total = Math.ceil((quantity ?? 0) * eaches);
  return <Text textType={TextType.Paragraph} value={total.toString()} />;
};

const createQuantityColumnDefinition = createColumnDefinitionBuilder({
  Header: <Text textType={TextType.Label} value={messages.qtyToTransferColumnHeader} />,
  accessor: 'quantity',
  Cell: QuantityCell,
  width: 120,
});

const UomNameCell = ({ row }: { row: ColumnRow }) => {
  const { unitsOfMeasure, unitOfMeasureId } = row.original;
  if (unitOfMeasureId == null) return null;
  const uom = unitOfMeasureId === 0 ? EACH_UOM : unitsOfMeasure?.find((u: UomInfo) => u.productUnitOfMeasureId === unitOfMeasureId);
  return <Text textType={TextType.Paragraph} value={uom?.uomTypeName ?? ''} />;
};

const ReadonlyEachesCell = ({ row }: { row: ColumnRow }) => {
  const { quantity, unitOfMeasureId, unitsOfMeasure } = row.original;
  if (unitOfMeasureId == null) return null;
  const rawUoms = unitsOfMeasure?.filter((u: UomInfo) => u.productUnitOfMeasureId !== 0) ?? [];
  const eaches = unitOfMeasureId === 0 ? 1 : computeAbsoluteEaches(unitOfMeasureId, rawUoms);
  const total = Math.ceil((quantity ?? 0) * eaches);
  return (
    <FlexColumn>
      <Text className={eachesStyles.total} element='span' textType={TextType.Paragraph} value={String(total)} />
      <Text className={eachesStyles.formula} element='span' textType={TextType.Caption} value={`(${quantity}×${eaches})`} />
    </FlexColumn>
  );
};

const createUomNameColumnDefinition = createColumnDefinitionBuilder({
  Header: <Text textType={TextType.Label} value={messages.unitOfMeasure} />,
  accessor: 'unitOfMeasureId',
  Cell: UomNameCell,
  width: 100,
});

const createReadonlyEachesColumnDefinition = createColumnDefinitionBuilder({
  Header: <Text textType={TextType.Label} value={messages.eaches} />,
  accessor: 'eaches',
  Cell: ReadonlyEachesCell,
  width: 120,
});

type GetReadonlyColumnsOptions = {
  showAdvancedColumns: boolean;
  isUomEnabled: boolean;
  openProductDetails: (productId: string) => void;
};

export const getReadonlyColumns = ({ showAdvancedColumns, isUomEnabled, openProductDetails }: GetReadonlyColumnsOptions) => {
  const advancedColumns = showAdvancedColumns ? [createLotIdColumnDefinition(), createExpirationDateColumnDefinition()] : [];
  const uomColumns = isUomEnabled ? [createUomNameColumnDefinition(), createReadonlyEachesColumnDefinition()] : [];

  return [
    createProductColumnDefinition(openProductDetails),
    createLocationColumnDefinition(),
    ...advancedColumns,
    ...uomColumns,
    createQuantityColumnDefinition(),
  ];
};
